package org.zq.linkgame.ui.model

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.remember

/**
 * Data class containing all translatable strings
 */
data class AppStrings(
    // App
    val appName: String,
    
    // Main Screen
    val mainTitle: String,
    val startGame: String,
    val levelSelect: String,
    val leaderboard: String,
    val contactUs: String,
    val feedback: String,
    val aboutUs: String,
    val settings: String,
    val backgroundTheme: String,
    val language: String,
    val close: String,
    
    // Game Screen
    val level: String, // "第%d关" or "Level %d"
    val selectDifficulty: String,
    val back: String,
    val hint: String,
    val bomb: String,
    val shuffle: String,
    val noConnectablePairs: String, // "没有可以连接的图案"
    val confirm: String, // "确定"
    
    // Difficulty
    val difficultyEasy: String,
    val difficultyHard: String,
    val difficultyExpert: String,
    val difficultyMaster: String,
    
    // Level Complete
    val levelComplete: String,
    val congratulations: String,
    val passedLevels: String, // "已通过%d关"
    val nextLevel: String,
    val returnHome: String,
    
    // Level Select Screen
    val allLevels: String,
    val levelCount: String, // "%d关"
    val pageInfo: String, // "%d / %d"
    
    // Level Categories
    val categoryAnimal: String = "Animal Paradise",
    val categoryFood: String = "Food World",
    val categoryFairyTale: String = "Fairy Tale",
    
    // Difficulty Info
    val passLevelsFormat: String, // "过%d关" or "Pass %d levels"
    val gainLevels: String, // "+%d关" or "+%d levels"
    val levelProgress: String, // "+X关 → 第Y关" or "+X → Level Y"
    
    // How to Play
    val howToPlay: String = "使用说明",
    val howToPlayStep1: String = "找到两个相同的图案",
    val howToPlayStep2: String = "点击它们进行连接",
    val howToPlayStep3: String = "连线不能超过两次转折且无阻挡",
    val howToPlayTip: String = "提示：使用道具可以帮助你更快通关！",
    
    // Leaderboard
    val rank: String = "排名",
    val player: String = "玩家",
    val score: String = "分数",
    val noRecords: String = "暂无记录",
    
    // Auth - Login/Register
    val login: String = "登录",
    val register: String = "注册",
    val username: String = "用户名",
    val phoneNumber: String = "手机号",
    val verificationCode: String = "验证码",
    val getCode: String = "获取验证码",
    val password: String = "密码",
    val confirmPassword: String = "确认密码",
    val privacyPolicy: String = "隐私协议",
    val userAgreement: String = "用户协议",
    val agreeTerms: String = "我已阅读并同意",
    val and: String = "和",
    val loginSuccess: String = "登录成功",
    val registerSuccess: String = "注册成功",
    val passwordMismatch: String = "两次密码不一致",
    val pleaseAgreeTerms: String = "请先同意协议",
    val pleaseEnterUsername: String = "请输入用户名",
    val pleaseEnterPhone: String = "请输入手机号",
    val pleaseEnterCode: String = "请输入验证码",
    val pleaseEnterPassword: String = "请输入密码",
    val codeSent: String = "验证码已发送",
    val selectCountryCode: String = "选择区号",
)

/**
 * CompositionLocal for providing strings throughout the app
 */
val LocalStrings = compositionLocalOf { getStringsForLanguage(AppLanguage.SYSTEM) }

/**
 * Get strings for a specific language
 */
fun getStringsForLanguage(language: AppLanguage): AppStrings {
    return when (language) {
        AppLanguage.CHINESE -> chineseStrings
        AppLanguage.ENGLISH -> englishStrings
        AppLanguage.FRENCH -> frenchStrings
        AppLanguage.GERMAN -> germanStrings
        AppLanguage.JAPANESE -> japaneseStrings
        AppLanguage.SPANISH -> spanishStrings
        AppLanguage.KOREAN -> koreanStrings
        AppLanguage.RUSSIAN -> russianStrings
        AppLanguage.PORTUGUESE -> portugueseStrings
        AppLanguage.ITALIAN -> italianStrings
        AppLanguage.ARABIC -> arabicStrings
        AppLanguage.THAI -> thaiStrings
        AppLanguage.VIETNAMESE -> vietnameseStrings
        AppLanguage.INDONESIAN -> indonesianStrings
        AppLanguage.TURKISH -> turkishStrings
        AppLanguage.DUTCH -> dutchStrings
        AppLanguage.POLISH -> polishStrings
        AppLanguage.GREEK -> greekStrings
        AppLanguage.HINDI -> hindiStrings
        AppLanguage.SYSTEM -> chineseStrings // Default to Chinese
    }
}

/**
 * Composable wrapper that provides localized strings
 */
@Composable
fun ProvideStrings(
    language: AppLanguage,
    content: @Composable () -> Unit
) {
    val strings = remember(language) { getStringsForLanguage(language) }
    CompositionLocalProvider(LocalStrings provides strings) {
        content()
    }
}

// ============== Language Definitions ==============

private val chineseStrings = AppStrings(
    appName = "智启连连看",
    mainTitle = "智启连连看",
    startGame = "开始游戏",
    levelSelect = "关卡选择",
    leaderboard = "排行榜",
    contactUs = "联系",
    feedback = "反馈",
    aboutUs = "关于我们",
    settings = "设置",
    backgroundTheme = "背景主题",
    language = "语言",
    close = "关闭",
    level = "第%d关",
    selectDifficulty = "选择难度",
    back = "返回",
    hint = "提示",
    bomb = "炸弹",
    shuffle = "洗牌",
    noConnectablePairs = "没有可以连接的图案",
    confirm = "确定",
    difficultyEasy = "简单",
    difficultyHard = "困难",
    difficultyExpert = "专家",
    difficultyMaster = "大师",
    levelComplete = "恭喜过关！",
    congratulations = "太棒了！",
    passedLevels = "已通过%d关",
    nextLevel = "下一关",
    returnHome = "返回首页",
    allLevels = "全部",
    levelCount = "%d关",
    pageInfo = "%d / %d",
    categoryAnimal = "动物乐园",
    categoryFood = "美食天地",
    categoryFairyTale = "奇幻童话",
    passLevelsFormat = "过%d关",
    gainLevels = "+%d关",
    levelProgress = "+%d关 → 第%d关",
    howToPlay = "使用说明",
    howToPlayStep1 = "找到两个相同的图案",
    howToPlayStep2 = "点击它们进行连接",
    howToPlayStep3 = "连线不能超过两次转折且无阻挡",
    howToPlayTip = "提示：使用道具可以帮助你更快通关！"
)

private val englishStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Link Game",
    startGame = "Start Game",
    levelSelect = "Level Select",
    leaderboard = "Leaderboard",
    contactUs = "Contact",
    feedback = "Feedback",
    aboutUs = "About Us",
    settings = "Settings",
    backgroundTheme = "Background Theme",
    language = "Language",
    close = "Close",
    level = "Level %d",
    selectDifficulty = "Select Difficulty",
    back = "Back",
    hint = "Hint",
    bomb = "Bomb",
    shuffle = "Shuffle",
    noConnectablePairs = "No connectable pairs",
    confirm = "OK",
    difficultyEasy = "Easy",
    difficultyHard = "Hard",
    difficultyExpert = "Expert",
    difficultyMaster = "Master",
    levelComplete = "Level Complete!",
    congratulations = "Congratulations!",
    passedLevels = "Passed %d levels",
    nextLevel = "Next Level",
    returnHome = "Return Home",
    allLevels = "All",
    levelCount = "%d levels",
    pageInfo = "%d / %d",
    categoryAnimal = "Animal Paradise",
    categoryFood = "Food World",
    categoryFairyTale = "Fairy Tale",
    passLevelsFormat = "Pass %d levels",
    gainLevels = "+%d levels",
    levelProgress = "+%d → Level %d",
    howToPlay = "How to Play",
    howToPlayStep1 = "Find two identical patterns",
    howToPlayStep2 = "Tap them to connect",
    howToPlayStep3 = "Line can't turn more than twice and must be unblocked",
    howToPlayTip = "Tip: Use props to help you pass levels faster!",
    login = "Login",
    register = "Register",
    username = "Username",
    phoneNumber = "Phone Number",
    verificationCode = "Verification Code",
    getCode = "Get Code",
    password = "Password",
    confirmPassword = "Confirm Password",
    privacyPolicy = "Privacy Policy",
    userAgreement = "User Agreement",
    agreeTerms = "I have read and agree to",
    and = "and",
    loginSuccess = "Login successful",
    registerSuccess = "Registration successful",
    passwordMismatch = "Passwords do not match",
    pleaseAgreeTerms = "Please agree to the terms",
    pleaseEnterUsername = "Please enter username",
    pleaseEnterPhone = "Please enter phone number",
    pleaseEnterCode = "Please enter verification code",
    pleaseEnterPassword = "Please enter password",
    codeSent = "Code sent",
    selectCountryCode = "Select Country Code"
)

private val frenchStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Jeu de Liens",
    startGame = "Commencer",
    levelSelect = "Niveaux",
    leaderboard = "Classement",
    contactUs = "Contact",
    feedback = "Avis",
    aboutUs = "À propos",
    settings = "Paramètres",
    backgroundTheme = "Thème de fond",
    language = "Langue",
    close = "Fermer",
    level = "Niveau %d",
    selectDifficulty = "Choisir difficulté",
    back = "Retour",
    hint = "Indice",
    bomb = "Bombe",
    shuffle = "Mélanger",
    noConnectablePairs = "Aucune paire connectable",
    confirm = "OK",
    difficultyEasy = "Facile",
    difficultyHard = "Difficile",
    difficultyExpert = "Expert",
    difficultyMaster = "Maître",
    levelComplete = "Niveau terminé!",
    congratulations = "Félicitations!",
    passedLevels = "%d niveaux réussis",
    nextLevel = "Niveau suivant",
    returnHome = "Accueil",
    allLevels = "Tous",
    levelCount = "%d niveaux",
    pageInfo = "%d / %d",
    passLevelsFormat = "Passer %d niveaux",
    gainLevels = "+%d niveaux",
    levelProgress = "+%d → Niveau %d",
    howToPlay = "Comment jouer",
    howToPlayStep1 = "Trouvez deux motifs identiques",
    howToPlayStep2 = "Touchez-les pour les connecter",
    howToPlayStep3 = "La ligne ne peut pas tourner plus de deux fois et doit être libre",
    howToPlayTip = "Astuce: Utilisez des accessoires pour passer les niveaux plus vite!"
)

private val germanStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Verbindungsspiel",
    startGame = "Spiel starten",
    levelSelect = "Level wählen",
    leaderboard = "Bestenliste",
    contactUs = "Kontakt",
    feedback = "Feedback",
    aboutUs = "Über uns",
    settings = "Einstellungen",
    backgroundTheme = "Hintergrundthema",
    language = "Sprache",
    close = "Schließen",
    level = "Level %d",
    selectDifficulty = "Schwierigkeit wählen",
    back = "Zurück",
    hint = "Hinweis",
    bomb = "Bombe",
    shuffle = "Mischen",
    noConnectablePairs = "Keine verbindbaren Paare",
    confirm = "OK",
    difficultyEasy = "Einfach",
    difficultyHard = "Schwer",
    difficultyExpert = "Experte",
    difficultyMaster = "Meister",
    levelComplete = "Level geschafft!",
    congratulations = "Herzlichen Glückwunsch!",
    passedLevels = "%d Level bestanden",
    nextLevel = "Nächstes Level",
    returnHome = "Startseite",
    allLevels = "Alle",
    levelCount = "%d Level",
    pageInfo = "%d / %d",
    passLevelsFormat = "%d Level bestehen",
    gainLevels = "+%d Level",
    levelProgress = "+%d → Level %d",
    howToPlay = "Spielanleitung",
    howToPlayStep1 = "Finden Sie zwei identische Muster",
    howToPlayStep2 = "Tippen Sie darauf, um sie zu verbinden",
    howToPlayStep3 = "Linie darf max. zwei Mal abbiegen und muss frei sein",
    howToPlayTip = "Tipp: Verwenden Sie Requisiten, um schneller zu bestehen!"
)

private val japaneseStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "つなげるゲーム",
    startGame = "ゲーム開始",
    levelSelect = "レベル選択",
    leaderboard = "ランキング",
    contactUs = "連絡",
    feedback = "フィードバック",
    aboutUs = "紹介",
    settings = "設定",
    backgroundTheme = "背景テーマ",
    language = "言語",
    close = "閉じる",
    level = "レベル %d",
    selectDifficulty = "難易度を選択",
    back = "戻る",
    hint = "ヒント",
    bomb = "爆弾",
    shuffle = "シャッフル",
    noConnectablePairs = "接続可能なペアがありません",
    confirm = "OK",
    difficultyEasy = "簡単",
    difficultyHard = "難しい",
    difficultyExpert = "エキスパート",
    difficultyMaster = "マスター",
    levelComplete = "クリア！",
    congratulations = "おめでとう！",
    passedLevels = "%dレベルクリア",
    nextLevel = "次のレベル",
    returnHome = "ホームへ",
    allLevels = "全て",
    levelCount = "%dレベル",
    pageInfo = "%d / %d",
    passLevelsFormat = "%dレベルクリア",
    gainLevels = "+%dレベル",
    levelProgress = "+%d → レベル%d",
    howToPlay = "遊び方",
    howToPlayStep1 = "同じ柄を2つ見つける",
    howToPlayStep2 = "タップして接続する",
    howToPlayStep3 = "線は2回以上曲がれず、障害物があってはいけない",
    howToPlayTip = "ヒント：アイテムを使うとより速くクリアできます！"
)

private val spanishStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Juego de Enlaces",
    startGame = "Iniciar Juego",
    levelSelect = "Seleccionar Nivel",
    leaderboard = "Clasificación",
    contactUs = "Contacto",
    feedback = "Comentarios",
    aboutUs = "Acerca de",
    settings = "Ajustes",
    backgroundTheme = "Tema de fondo",
    language = "Idioma",
    close = "Cerrar",
    level = "Nivel %d",
    selectDifficulty = "Seleccionar dificultad",
    back = "Volver",
    hint = "Pista",
    bomb = "Bomba",
    shuffle = "Barajar",
    noConnectablePairs = "No hay pares conectables",
    confirm = "OK",
    difficultyEasy = "Fácil",
    difficultyHard = "Difícil",
    difficultyExpert = "Experto",
    difficultyMaster = "Maestro",
    levelComplete = "¡Nivel completado!",
    congratulations = "¡Felicitaciones!",
    passedLevels = "%d niveles pasados",
    nextLevel = "Siguiente nivel",
    returnHome = "Inicio",
    allLevels = "Todos",
    levelCount = "%d niveles",
    pageInfo = "%d / %d",
    passLevelsFormat = "Pasar %d niveles",
    gainLevels = "+%d niveles",
    levelProgress = "+%d → Nivel %d",
    howToPlay = "Cómo jugar",
    howToPlayStep1 = "Encuentra dos patrones idénticos",
    howToPlayStep2 = "Tócalos para conectarlos",
    howToPlayStep3 = "La línea no puede girar más de dos veces y debe estar libre",
    howToPlayTip = "Consejo: ¡Usa accesorios para pasar niveles más rápido!"
)

private val koreanStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "연결 게임",
    startGame = "게임 시작",
    levelSelect = "레벨 선택",
    leaderboard = "순위표",
    contactUs = "연락처",
    feedback = "피드백",
    aboutUs = "소개",
    settings = "설정",
    backgroundTheme = "배경 테마",
    language = "언어",
    close = "닫기",
    level = "레벨 %d",
    selectDifficulty = "난이도 선택",
    back = "뒤로",
    hint = "힌트",
    bomb = "폭탄",
    shuffle = "섞기",
    noConnectablePairs = "연결 가능한 쌍이 없습니다",
    confirm = "확인",
    difficultyEasy = "쉬움",
    difficultyHard = "어려움",
    difficultyExpert = "전문가",
    difficultyMaster = "마스터",
    levelComplete = "레벨 완료!",
    congratulations = "축하합니다!",
    passedLevels = "%d 레벨 통과",
    nextLevel = "다음 레벨",
    returnHome = "홈으로",
    allLevels = "전체",
    levelCount = "%d 레벨",
    pageInfo = "%d / %d",
    passLevelsFormat = "%d 레벨 클리어",
    gainLevels = "+%d 레벨",
    levelProgress = "+%d → 레벨 %d",
    howToPlay = "게임 방법",
    howToPlayStep1 = "동일한 패턴 두 개 찾기",
    howToPlayStep2 = "탭하여 연결하기",
    howToPlayStep3 = "선은 두 번 이상 꺾일 수 없고 장애물이 없어야 함",
    howToPlayTip = "팁: 아이템을 사용하면 더 빨리 클리어할 수 있습니다!"
)

private val russianStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Игра связей",
    startGame = "Начать игру",
    levelSelect = "Выбор уровня",
    leaderboard = "Таблица лидеров",
    contactUs = "Контакт",
    feedback = "Отзыв",
    aboutUs = "О нас",
    settings = "Настройки",
    backgroundTheme = "Тема фона",
    language = "Язык",
    close = "Закрыть",
    level = "Уровень %d",
    selectDifficulty = "Выбрать сложность",
    back = "Назад",
    hint = "Подсказка",
    bomb = "Бомба",
    shuffle = "Перемешать",
    noConnectablePairs = "Нет соединяемых пар",
    confirm = "OK",
    difficultyEasy = "Легко",
    difficultyHard = "Сложно",
    difficultyExpert = "Эксперт",
    difficultyMaster = "Мастер",
    levelComplete = "Уровень пройден!",
    congratulations = "Поздравляем!",
    passedLevels = "Пройдено %d уровней",
    nextLevel = "Следующий уровень",
    returnHome = "На главную",
    allLevels = "Все",
    levelCount = "%d уровней",
    pageInfo = "%d / %d",
    passLevelsFormat = "Пройти %d уровней",
    gainLevels = "+%d уровней",
    levelProgress = "+%d → Уровень %d",
    howToPlay = "Как играть",
    howToPlayStep1 = "Найдите два одинаковых узора",
    howToPlayStep2 = "Нажмите на них, чтобы соединить",
    howToPlayStep3 = "Линия не может поворачивать более двух раз и должна быть свободной",
    howToPlayTip = "Совет: Используйте предметы, чтобы пройти уровни быстрее!"
)

private val portugueseStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Jogo de Ligação",
    startGame = "Iniciar Jogo",
    levelSelect = "Selecionar Nível",
    leaderboard = "Classificação",
    contactUs = "Contato",
    feedback = "Feedback",
    aboutUs = "Sobre nós",
    settings = "Configurações",
    backgroundTheme = "Tema de fundo",
    language = "Idioma",
    close = "Fechar",
    level = "Nível %d",
    selectDifficulty = "Selecionar dificuldade",
    back = "Voltar",
    hint = "Dica",
    bomb = "Bomba",
    shuffle = "Embaralhar",
    noConnectablePairs = "Não há pares conectáveis",
    confirm = "OK",
    difficultyEasy = "Fácil",
    difficultyHard = "Difícil",
    difficultyExpert = "Especialista",
    difficultyMaster = "Mestre",
    levelComplete = "Nível completo!",
    congratulations = "Parabéns!",
    passedLevels = "%d níveis passados",
    nextLevel = "Próximo nível",
    returnHome = "Início",
    allLevels = "Todos",
    levelCount = "%d níveis",
    pageInfo = "%d / %d",
    passLevelsFormat = "Passar %d níveis",
    gainLevels = "+%d níveis",
    levelProgress = "+%d → Nível %d",
    howToPlay = "Como jogar",
    howToPlayStep1 = "Encontre dois padrões idênticos",
    howToPlayStep2 = "Toque neles para conectar",
    howToPlayStep3 = "A linha não pode virar mais de duas vezes e deve estar livre",
    howToPlayTip = "Dica: Use itens para passar de nível mais rápido!"
)

private val italianStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Gioco di Collegamenti",
    startGame = "Inizia Gioco",
    levelSelect = "Seleziona Livello",
    leaderboard = "Classifica",
    contactUs = "Contatto",
    feedback = "Feedback",
    aboutUs = "Chi siamo",
    settings = "Impostazioni",
    backgroundTheme = "Tema sfondo",
    language = "Lingua",
    close = "Chiudi",
    level = "Livello %d",
    selectDifficulty = "Seleziona difficoltà",
    back = "Indietro",
    hint = "Suggerimento",
    bomb = "Bomba",
    shuffle = "Mescola",
    noConnectablePairs = "Nessuna coppia collegabile",
    confirm = "OK",
    difficultyEasy = "Facile",
    difficultyHard = "Difficile",
    difficultyExpert = "Esperto",
    difficultyMaster = "Maestro",
    levelComplete = "Livello completato!",
    congratulations = "Congratulazioni!",
    passedLevels = "%d livelli superati",
    nextLevel = "Prossimo livello",
    returnHome = "Home",
    allLevels = "Tutti",
    levelCount = "%d livelli",
    pageInfo = "%d / %d",
    passLevelsFormat = "Passare %d livelli",
    gainLevels = "+%d livelli",
    levelProgress = "+%d → Livello %d",
    howToPlay = "Come giocare",
    howToPlayStep1 = "Trova due motivi identici",
    howToPlayStep2 = "Toccali per collegarli",
    howToPlayStep3 = "La linea non può girare più di due volte e deve essere libera",
    howToPlayTip = "Suggerimento: Usa gli oggetti per superare i livelli più velocemente!"
)

private val arabicStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "لعبة الربط",
    startGame = "ابدأ اللعبة",
    levelSelect = "اختر المستوى",
    leaderboard = "لوحة المتصدرين",
    contactUs = "اتصل بنا",
    feedback = "ملاحظات",
    aboutUs = "معلومات عنا",
    settings = "الإعدادات",
    backgroundTheme = "سمة الخلفية",
    language = "اللغة",
    close = "إغلاق",
    level = "المستوى %d",
    selectDifficulty = "اختر الصعوبة",
    back = "رجوع",
    hint = "تلميح",
    bomb = "قنبلة",
    shuffle = "خلط",
    noConnectablePairs = "لا توجد أزواج قابلة للربط",
    confirm = "حسنًا",
    difficultyEasy = "سهل",
    difficultyHard = "صعب",
    difficultyExpert = "خبير",
    difficultyMaster = "محترف",
    levelComplete = "اكتمل المستوى!",
    congratulations = "تهانينا!",
    passedLevels = "اجتزت %d مستوى",
    nextLevel = "المستوى التالي",
    returnHome = "الرئيسية",
    allLevels = "الكل",
    levelCount = "%d مستوى",
    pageInfo = "%d / %d",
    passLevelsFormat = "اجتياز %d مستوى",
    gainLevels = "+%d مستوى",
    levelProgress = "+%d → المستوى %d",
    howToPlay = "كيفية اللعب",
    howToPlayStep1 = "ابحث عن نمطين متطابقين",
    howToPlayStep2 = "اضغط عليهما للربط",
    howToPlayStep3 = "لا يمكن للخط أن ينعطف أكثر من مرتين ويجب أن يكون خالياً",
    howToPlayTip = "نصيحة: استخدم الأدوات لتجاوز المستويات بشكل أسرع!"
)

private val thaiStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "เกมเชื่อมต่อ",
    startGame = "เริ่มเกม",
    levelSelect = "เลือกด่าน",
    leaderboard = "อันดับ",
    contactUs = "ติดต่อ",
    feedback = "ความคิดเห็น",
    aboutUs = "เกี่ยวกับเรา",
    settings = "ตั้งค่า",
    backgroundTheme = "ธีมพื้นหลัง",
    language = "ภาษา",
    close = "ปิด",
    level = "ด่าน %d",
    selectDifficulty = "เลือกความยาก",
    back = "กลับ",
    hint = "คำใบ้",
    bomb = "ระเบิด",
    shuffle = "สับ",
    noConnectablePairs = "ไม่มีคู่ที่เชื่อมต่อได้",
    confirm = "ตกลง",
    difficultyEasy = "ง่าย",
    difficultyHard = "ยาก",
    difficultyExpert = "ผู้เชี่ยวชาญ",
    difficultyMaster = "ปรมาจารย์",
    levelComplete = "ผ่านด่าน!",
    congratulations = "ยินดีด้วย!",
    passedLevels = "ผ่าน %d ด่าน",
    nextLevel = "ด่านถัดไป",
    returnHome = "หน้าหลัก",
    allLevels = "ทั้งหมด",
    levelCount = "%d ด่าน",
    pageInfo = "%d / %d",
    passLevelsFormat = "ผ่าน %d ด่าน",
    gainLevels = "+%d ด่าน",
    levelProgress = "+%d → ด่าน %d",
    howToPlay = "วิธีเล่น",
    howToPlayStep1 = "หารูปแบบที่เหมือนกันสองอัน",
    howToPlayStep2 = "แตะเพื่อเชื่อมต่อ",
    howToPlayStep3 = "เส้นหักได้ไม่เกินสองครั้งและต้องไม่มีสิ่งกีดขวาง",
    howToPlayTip = "เคล็ดลับ: ใช้ไอเทมเพื่อผ่านด่านได้เร็วขึ้น!"
)

private val vietnameseStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Trò chơi nối",
    startGame = "Bắt đầu",
    levelSelect = "Chọn màn",
    leaderboard = "Bảng xếp hạng",
    contactUs = "Liên hệ",
    feedback = "Phản hồi",
    aboutUs = "Về chúng tôi",
    settings = "Cài đặt",
    backgroundTheme = "Chủ đề nền",
    language = "Ngôn ngữ",
    close = "Đóng",
    level = "Màn %d",
    selectDifficulty = "Chọn độ khó",
    back = "Quay lại",
    hint = "Gợi ý",
    bomb = "Bom",
    shuffle = "Xáo trộn",
    noConnectablePairs = "Không có cặp nào có thể kết nối",
    confirm = "OK",
    difficultyEasy = "Dễ",
    difficultyHard = "Khó",
    difficultyExpert = "Chuyên gia",
    difficultyMaster = "Bậc thầy",
    levelComplete = "Hoàn thành!",
    congratulations = "Chúc mừng!",
    passedLevels = "Đã qua %d màn",
    nextLevel = "Màn tiếp theo",
    returnHome = "Trang chủ",
    allLevels = "Tất cả",
    levelCount = "%d màn",
    pageInfo = "%d / %d",
    passLevelsFormat = "Qua %d màn",
    gainLevels = "+%d màn",
    levelProgress = "+%d → Màn %d",
    howToPlay = "Cách chơi",
    howToPlayStep1 = "Tìm hai hình giống nhau",
    howToPlayStep2 = "Chạm vào chúng để kết nối",
    howToPlayStep3 = "Đường nối không được gấp quá hai lần và phải trống",
    howToPlayTip = "Mẹo: Sử dụng đạo cụ để qua màn nhanh hơn!"
)

private val indonesianStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Permainan Sambung",
    startGame = "Mulai",
    levelSelect = "Pilih Level",
    leaderboard = "Papan Peringkat",
    contactUs = "Kontak",
    feedback = "Masukan",
    aboutUs = "Tentang Kami",
    settings = "Pengaturan",
    backgroundTheme = "Tema Latar",
    language = "Bahasa",
    close = "Tutup",
    level = "Level %d",
    selectDifficulty = "Pilih kesulitan",
    back = "Kembali",
    hint = "Petunjuk",
    bomb = "Bom",
    shuffle = "Acak",
    noConnectablePairs = "Tidak ada pasangan yang dapat dihubungkan",
    confirm = "OK",
    difficultyEasy = "Mudah",
    difficultyHard = "Sulit",
    difficultyExpert = "Ahli",
    difficultyMaster = "Master",
    levelComplete = "Level selesai!",
    congratulations = "Selamat!",
    passedLevels = "%d level selesai",
    nextLevel = "Level berikutnya",
    returnHome = "Beranda",
    allLevels = "Semua",
    levelCount = "%d level",
    pageInfo = "%d / %d",
    passLevelsFormat = "Selesai %d level",
    gainLevels = "+%d level",
    levelProgress = "+%d → Level %d",
    howToPlay = "Cara Bermain",
    howToPlayStep1 = "Temukan dua pola yang sama",
    howToPlayStep2 = "Ketuk untuk menghubungkan",
    howToPlayStep3 = "Garis tidak boleh berbelok lebih dari dua kali dan harus bebas",
    howToPlayTip = "Tips: Gunakan item untuk melewati level lebih cepat!"
)

private val turkishStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Bağlantı Oyunu",
    startGame = "Oyunu Başlat",
    levelSelect = "Seviye Seç",
    leaderboard = "Skor Tablosu",
    contactUs = "İletişim",
    feedback = "Geri Bildirim",
    aboutUs = "Hakkımızda",
    settings = "Ayarlar",
    backgroundTheme = "Arka Plan Teması",
    language = "Dil",
    close = "Kapat",
    level = "Seviye %d",
    selectDifficulty = "Zorluk seç",
    back = "Geri",
    hint = "İpucu",
    bomb = "Bomba",
    shuffle = "Karıştır",
    noConnectablePairs = "Bağlanabilir çift yok",
    confirm = "Tamam",
    difficultyEasy = "Kolay",
    difficultyHard = "Zor",
    difficultyExpert = "Uzman",
    difficultyMaster = "Usta",
    levelComplete = "Seviye tamamlandı!",
    congratulations = "Tebrikler!",
    passedLevels = "%d seviye geçildi",
    nextLevel = "Sonraki seviye",
    returnHome = "Ana sayfa",
    allLevels = "Tümü",
    levelCount = "%d seviye",
    pageInfo = "%d / %d",
    passLevelsFormat = "%d seviye geç",
    gainLevels = "+%d seviye",
    levelProgress = "+%d → Seviye %d",
    howToPlay = "Nasıl Oynanır",
    howToPlayStep1 = "Aynı iki deseni bulun",
    howToPlayStep2 = "Bağlamak için dokunun",
    howToPlayStep3 = "Çizgi ikiden fazla dönüş yapamaz ve engelsiz olmalı",
    howToPlayTip = "İpucu: Seviyeleri daha hızlı geçmek için eşyaları kullanın!"
)

private val dutchStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Verbindingsspel",
    startGame = "Start Spel",
    levelSelect = "Niveau kiezen",
    leaderboard = "Ranglijst",
    contactUs = "Contact",
    feedback = "Feedback",
    aboutUs = "Over ons",
    settings = "Instellingen",
    backgroundTheme = "Achtergrondthema",
    language = "Taal",
    close = "Sluiten",
    level = "Niveau %d",
    selectDifficulty = "Kies moeilijkheid",
    back = "Terug",
    hint = "Hint",
    bomb = "Bom",
    shuffle = "Schudden",
    noConnectablePairs = "Geen koppelbare paren",
    confirm = "OK",
    difficultyEasy = "Makkelijk",
    difficultyHard = "Moeilijk",
    difficultyExpert = "Expert",
    difficultyMaster = "Meester",
    levelComplete = "Niveau voltooid!",
    congratulations = "Gefeliciteerd!",
    passedLevels = "%d niveaus gehaald",
    nextLevel = "Volgend niveau",
    returnHome = "Startpagina",
    allLevels = "Alle",
    levelCount = "%d niveaus",
    pageInfo = "%d / %d",
    passLevelsFormat = "%d niveaus halen",
    gainLevels = "+%d niveaus",
    levelProgress = "+%d → Niveau %d",
    howToPlay = "Hoe te spelen",
    howToPlayStep1 = "Vind twee identieke patronen",
    howToPlayStep2 = "Tik om te verbinden",
    howToPlayStep3 = "Lijn mag max twee keer draaien en moet vrij zijn",
    howToPlayTip = "Tip: Gebruik items om sneller niveaus te halen!"
)

private val polishStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Gra w łączenie",
    startGame = "Rozpocznij grę",
    levelSelect = "Wybierz poziom",
    leaderboard = "Ranking",
    contactUs = "Kontakt",
    feedback = "Opinia",
    aboutUs = "O nas",
    settings = "Ustawienia",
    backgroundTheme = "Motyw tła",
    language = "Język",
    close = "Zamknij",
    level = "Poziom %d",
    selectDifficulty = "Wybierz trudność",
    back = "Wstecz",
    hint = "Podpowiedź",
    bomb = "Bomba",
    shuffle = "Tasuj",
    noConnectablePairs = "Brak połączalnych par",
    confirm = "OK",
    difficultyEasy = "Łatwy",
    difficultyHard = "Trudny",
    difficultyExpert = "Ekspert",
    difficultyMaster = "Mistrz",
    levelComplete = "Poziom ukończony!",
    congratulations = "Gratulacje!",
    passedLevels = "Ukończono %d poziomów",
    nextLevel = "Następny poziom",
    returnHome = "Strona główna",
    allLevels = "Wszystkie",
    levelCount = "%d poziomów",
    pageInfo = "%d / %d",
    passLevelsFormat = "Ukończ %d poziomów",
    gainLevels = "+%d poziomów",
    levelProgress = "+%d → Poziom %d",
    howToPlay = "Jak grać",
    howToPlayStep1 = "Znajdź dwa identyczne wzory",
    howToPlayStep2 = "Dotknij, aby połączyć",
    howToPlayStep3 = "Linia nie może skręcać więcej niż dwa razy i musi być wolna",
    howToPlayTip = "Wskazówka: Użyj przedmiotów, aby szybciej przejść poziomy!"
)

private val greekStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "Παιχνίδι Σύνδεσης",
    startGame = "Έναρξη",
    levelSelect = "Επιλογή επιπέδου",
    leaderboard = "Κατάταξη",
    contactUs = "Επικοινωνία",
    feedback = "Σχόλια",
    aboutUs = "Σχετικά με εμάς",
    settings = "Ρυθμίσεις",
    backgroundTheme = "Θέμα φόντου",
    language = "Γλώσσα",
    close = "Κλείσιμο",
    level = "Επίπεδο %d",
    selectDifficulty = "Επιλογή δυσκολίας",
    back = "Πίσω",
    hint = "Υπόδειξη",
    bomb = "Βόμβα",
    shuffle = "Ανακάτεμα",
    noConnectablePairs = "Δεν υπάρχουν συνδέσιμα ζεύγη",
    confirm = "Εντάξει",
    difficultyEasy = "Εύκολο",
    difficultyHard = "Δύσκολο",
    difficultyExpert = "Ειδικός",
    difficultyMaster = "Μάστερ",
    levelComplete = "Επίπεδο ολοκληρώθηκε!",
    congratulations = "Συγχαρητήρια!",
    passedLevels = "%d επίπεδα περάστηκαν",
    nextLevel = "Επόμενο επίπεδο",
    returnHome = "Αρχική",
    allLevels = "Όλα",
    levelCount = "%d επίπεδα",
    pageInfo = "%d / %d",
    passLevelsFormat = "Περάστε %d επίπεδα",
    gainLevels = "+%d επίπεδα",
    levelProgress = "+%d → Επίπεδο %d",
    howToPlay = "Πώς να παίξετε",
    howToPlayStep1 = "Βρείτε δύο πανομοιότυπα μοτίβα",
    howToPlayStep2 = "Πατήστε για σύνδεση",
    howToPlayStep3 = "Η γραμμή δεν μπορεί να στρίψει πάνω από δύο φορές και πρέπει να είναι ελεύθερη",
    howToPlayTip = "Συμβουλή: Χρησιμοποιήστε αντικείμενα για να περάσετε επίπεδα πιο γρήγορα!"
)

private val hindiStrings = AppStrings(
    appName = "LinkGame",
    mainTitle = "लिंक गेम",
    startGame = "खेल शुरू करें",
    levelSelect = "स्तर चुनें",
    leaderboard = "लीडरबोर्ड",
    contactUs = "संपर्क करें",
    feedback = "प्रतिक्रिया",
    aboutUs = "हमारे बारे में",
    settings = "सेटिंग्स",
    backgroundTheme = "पृष्ठभूमि थीम",
    language = "भाषा",
    close = "बंद करें",
    level = "स्तर %d",
    selectDifficulty = "कठिनाई चुनें",
    back = "वापस",
    hint = "संकेत",
    bomb = "बम",
    shuffle = "फेरबदल",
    noConnectablePairs = "कोई जोड़ी जोड़ने योग्य नहीं",
    confirm = "ठीक है",
    difficultyEasy = "आसान",
    difficultyHard = "कठिन",
    difficultyExpert = "विशेषज्ञ",
    difficultyMaster = "मास्टर",
    levelComplete = "स्तर पूर्ण!",
    congratulations = "बधाई हो!",
    passedLevels = "%d स्तर पास",
    nextLevel = "अगला स्तर",
    returnHome = "होम पेज",
    allLevels = "सभी",
    levelCount = "%d स्तर",
    pageInfo = "%d / %d",
    passLevelsFormat = "%d स्तर पास",
    gainLevels = "+%d स्तर",
    levelProgress = "+%d → स्तर %d",
    howToPlay = "कैसे खेलें",
    howToPlayStep1 = "दो समान पैटर्न खोजें",
    howToPlayStep2 = "जोड़ने के लिए टैप करें",
    howToPlayStep3 = "रेखा दो बार से अधिक नहीं मुड़ सकती और खाली होनी चाहिए",
    howToPlayTip = "टिप: स्तरों को तेज़ी से पास करने के लिए आइटम का उपयोग करें!"
)
